'use client'

import { useSearchParams } from 'next/navigation'
/* eslint-disable react/no-danger */
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
/* eslint-disable @typescript-eslint/no-floating-promises */

import ButtonWithDisclaimer from '@components/common/AutoWidgetButton/buttonWithDisclaimer'
import IllogicalFlowModal from '@components/common/IllogicalFlowModal/illogicalFlowModal'
import NewFlowBanner from '@components/common/NewFlowBanner/newFlowBanner'
import { getR2d2Payload } from '@utils/r2d2Payload'
import { segmentEventCommonPropFunc, semAutoR2d2properties } from '@utils/segmentProperties'
import React, { useEffect, useState } from 'react'
import { AutoProduct, AutoWidgetVariant } from 'src/enums/AutoProduct'
import { ButtonType } from 'src/enums/ButtonType'
import { REGISTRATION_NUMBER_QUERY_KEY } from '@constants/queryParamsKeys'
import { useUserContext } from '@context/UserContext/UserContenxt'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import { getConfig } from '@configs/envConfig'
import { preloadTurnstile } from '@utils/turnstile/turnstile'
import styles from './autoWidget.module.scss'
import {
  enterJourneyEkind,
  enterJourneyEventName,
  autoWidgetJourney,
  newFlowJourneyEkind,
  newFlowJourneyEventName,
  newFlowJourney,
} from './config'
import { handleRegNumberValidation, handleSubmit } from './utils'
import { type AutoWidgetProps } from './interfaces'

export const proposalAPIResponseInitial = {
  error_context: '',
  error_message: '',
  error_code: '',
  request: { response: '' },
}

const proposalAPIUrl = '/motororchestrator/api/v2/proposals'

const AutoWidgetNewLayout = (props: AutoWidgetProps) => {
  const {
    widgetHeading,
    disclaimer,
    inputPlaceholder,
    widgetCtaText,
    segmentEventCommonProperties,
    newFlowBanner,
    widgetType,
    product,
    newFlowRedirectionUrl,
    widgetVariant,
    isBottomFloater,
    isSemPage,
    r2d2Commonproperties,
    regexPatternsData,
  } = props

  const queryParams = useSearchParams()
  const [regNo, setRegNo] = useState(queryParams.get(REGISTRATION_NUMBER_QUERY_KEY) || '')
  const [isFetching, setIsFetching] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errors, setErrors] = useState({})
  const [showIllogicalFlowModal, setShowIllogicalFlowModal] = useState(false)
  const [proposalAPIResponse, setProposalAPIResponse] = useState(proposalAPIResponseInitial)
  const [properties, setProperties] = useState({})
  const [newFlowR2d2Props, setNewFlowR2d2Props] = useState({})
  const { userData } = useUserContext()
  const envConfig = useEnvVariablesClientConfig()
  const turnstileConfig = getConfig(envConfig.ACKO_ENV)?.turnstile

  const additionalProperties = {
    cta_text: widgetCtaText,
    funnel: 'entry',
    communication_pincode: '',
    widget_type: 'purple_widget',
    journey: autoWidgetJourney[product][widgetType],
  }

  const fetchAndInvokeHandleSubmit = async () => {
    await handleSubmit({
      proposalAPIUrl,
      regNo,
      userData,
      setIsFetching,
      setErrors,
      setProposalAPIResponse,
      setShowIllogicalFlowModal,
      InsuretrackEventName: enterJourneyEventName[product][widgetType],
      InsureSegmentTrackProps: {
        ...properties,
        ...additionalProperties,
        ...getR2d2Payload({
          isSemPage,
          ekind: enterJourneyEkind[product][widgetType],
          edata: {
            product_state: isSemPage ? 'Marketing Landing Pages' : null,
            screen: 'registration_number_screen',
            registration_number: regNo,
          },
          odata: { product },
        }),
      },
      inputCtaText: widgetCtaText,
      product,
      setShowError,
      regexPatternsData,
      turnstileConfig,
    })
    console.log('Errors are: ', JSON.stringify(errors))
  }

  useEffect(() => {
    void preloadTurnstile(turnstileConfig || {}).catch(error => {
      console.error('Turnstile preload failed:', error)
    })
  }, [turnstileConfig])

  useEffect(() => {
    const getProperties = async () => {
      const props = await segmentEventCommonPropFunc(segmentEventCommonProperties)
      setProperties({ ...props })
    }
    getProperties()
    setNewFlowR2d2Props(
      getR2d2Payload({
        ekind: newFlowJourneyEkind[product][widgetType],
        edata: { journey: product === AutoProduct.Car ? 'new_car' : 'new_bike', lob: 'direct', pa_cover: false },
        odata: { product },
        r2d2CommonProperties: r2d2Commonproperties,
      })
    )
  }, [])

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShowError(true)
      isSemPage &&
        trackEvent('error_triggered_on_cta', {
          ...properties,
          ...additionalProperties,
          ...getR2d2Payload({
            isSemPage,
            ekind: 'invalid_reg_number',
            edata: { ...semAutoR2d2properties },
            odata: { product },
          }),
        })
    } else {
      setShowError(false)
    }
  }, [errors])

  useEffect(() => {
    // Using this to prevent scrolling when overlay is open
    showIllogicalFlowModal
      ? (document.documentElement.style.overflowY = 'hidden')
      : (document.documentElement.style.overflowY = 'scroll')
  }, [showIllogicalFlowModal])

  const onBlurSegmentEvent = async () => {
    const eventProperties = {
      ...properties,
      ...additionalProperties,
      registration_number: regNo,
      ...getR2d2Payload({
        ekind: product === AutoProduct.Car ? 'registration_number_entered' : 'tap_btn_enter_your_bike_number',
        edata: { ...semAutoR2d2properties },
        odata: { product },
      }),
    }
    if (isSemPage) {
      await trackEvent('tap_registration_number_cta', eventProperties)
    }
  }

  return (
    <div className={`${styles.autoWidgetRootContainer} ${isBottomFloater ? styles.bottomFloaterRoot : ''}`}>
      <div
        className={`${styles.widgetContainer} ${isBottomFloater ? styles.bottomFloater : ''} ${
          widgetVariant === AutoWidgetVariant.SemVariant ? styles.semVariant : styles.seoVariant
        }`}>
        <div className={`${styles.widgetHeading} ${isBottomFloater ? styles.bottomFloaterHeading : ''}`}>
          {widgetHeading}
        </div>
        <div className={styles.inputContainer}>
          <input
            className={styles.input}
            type="text"
            placeholder={inputPlaceholder || `Enter your ${product} number`}
            name={`${product}Number`}
            value={regNo}
            onChange={event => {
              setRegNo(event.target.value.toUpperCase())
              if (Object.entries(errors).length > 0) {
                setErrors({})
              }
            }}
            onBlur={() => {
              onBlurSegmentEvent()
              regNo && handleRegNumberValidation(regNo, setErrors, product, regexPatternsData)
            }}
          />
          {showError ? <div className={styles.errorContainer}>{errors[product]}</div> : null}
        </div>
        <div className={styles.showOnlyWeb}>
          <ButtonWithDisclaimer
            ctaText={widgetCtaText}
            onClick={fetchAndInvokeHandleSubmit}
            type={ButtonType.Green}
            loading={isFetching}
            disabled={isFetching}
          />
        </div>

        <div className={styles.showOnlyMweb}>
          <ButtonWithDisclaimer
            disclaimer={disclaimer}
            ctaText={widgetCtaText}
            onClick={fetchAndInvokeHandleSubmit}
            type={ButtonType.Green}
            loading={isFetching}
            disabled={isFetching}
          />
        </div>
      </div>

      <NewFlowBanner
        title={newFlowBanner?.heading || `Buying a brand new ${product}?`}
        subTitle={newFlowBanner?.subHeading || `Great discount’s on your new ${product} insurance`}
        ctaText={newFlowBanner?.ctaText || 'View plans'}
        redirectionUrl={newFlowRedirectionUrl}
        icon={{
          src: newFlowBanner?.iconSrc,
          alt: newFlowBanner?.iconAlt,
          position: newFlowBanner?.iconPosition,
          width: 72,
          height: 54,
        }}
        InsuretrackEventName={newFlowJourneyEventName[product][widgetType]}
        InsureSegmentTrackProps={{
          ...properties,
          ...additionalProperties,
          ...newFlowR2d2Props,
          journey: newFlowJourney[product][widgetType],
        }}
        widgetVariant={widgetVariant}
        isBottomFloater={isBottomFloater}
      />
      {showIllogicalFlowModal ? (
        <IllogicalFlowModal
          regNo={regNo}
          errorCode={proposalAPIResponse?.error_code}
          illogicalFlowData={proposalAPIResponse?.error_context}
          setProposalAPIResponse={setProposalAPIResponse}
          product={product}
          showIllogicalFlowModal={showIllogicalFlowModal}
          setShowIllogicalFlowModal={setShowIllogicalFlowModal}
          secondaryCta={() => {
            setShowIllogicalFlowModal(false)
            setProposalAPIResponse(proposalAPIResponseInitial)
          }}
          onClose={() => {
            setShowIllogicalFlowModal(false)
            setProposalAPIResponse(proposalAPIResponseInitial)
          }}
          identifiedVehicleType={proposalAPIResponse?.error_context}
        />
      ) : null}
    </div>
  )
}

export default AutoWidgetNewLayout
